import { runInAction } from "mobx";
import type { ChangeEvent } from "react";
import type {
  AiBoxDraft,
  BoxListItem,
  IWarehouseApi,
} from "../../../api/WarehouseApi";
import { Box } from "../../../stores/Box";
import { type INavigationStore, Screens } from "../../../stores/navigation";
import type { IStore } from "../../../stores/store";
import type { AddProductDraft } from "../../AddProductPage/AddProductPageViewModel";
import { applyAiBoxDraft, type AppliedAiBoxDraft } from "./ai-draft";
import { buildCreateBoxPayload, validateBoxForm } from "./form";
import { fileToDataUrl, validatePhotoFile } from "./photo-upload";
import { buildReferenceData } from "./reference-data";

export interface AddBoxWorkflowState {
  name: string;
  description: string;
  photo: string;
  typeTags: string[];
  typeTagInput: string;
  parentId: string;
  availableBoxes: BoxListItem[];
  typeTagSuggestions: string[];
  isReferenceLoading: boolean;
  referenceError: string | null;
  isPhotoLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  voiceTranscript: string;
  isVoicePanelOpen: boolean;
  isVoiceApplying: boolean;
  voiceError: string | null;
  voiceInfo: string | null;
}

export interface AddBoxPhotoAiSuggestion {
  model: string;
  draft: AiBoxDraft;
  patch: AppliedAiBoxDraft;
  changes: string[];
  usedTranscriptContext: boolean;
}

function resolvePhotoForAiAutofill(state: AddBoxWorkflowState): string | null {
  const photo = state.photo.trim();
  return photo || null;
}

function resolveAddBoxErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function formatTextDiff(before: string, after: string): string {
  const current = before.trim() || "—";
  const next = after.trim() || "—";
  return `"${current}" → "${next}"`;
}

function formatTypeTags(tags: string[]): string {
  if (tags.length === 0) {
    return "—";
  }

  return tags.map((tag) => `#${tag}`).join(" ");
}

function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
}

function buildBoxAiSuggestionChanges(input: {
  state: AddBoxWorkflowState;
  patch: AppliedAiBoxDraft;
}): string[] {
  const changes: string[] = [];

  if (input.patch.name && input.patch.name !== input.state.name) {
    changes.push(
      `Название: ${formatTextDiff(input.state.name, input.patch.name)}`,
    );
  }

  if (
    input.patch.description &&
    input.patch.description !== input.state.description
  ) {
    changes.push(
      `Описание: ${formatTextDiff(input.state.description, input.patch.description)}`,
    );
  }

  if (input.patch.photo && input.patch.photo !== input.state.photo) {
    changes.push(
      `Фото: ${formatTextDiff(input.state.photo, input.patch.photo)}`,
    );
  }

  if (
    input.patch.typeTags &&
    !areStringArraysEqual(input.patch.typeTags, input.state.typeTags)
  ) {
    changes.push(
      `Теги: ${formatTypeTags(input.state.typeTags)} → ${formatTypeTags(input.patch.typeTags)}`,
    );
  }

  if (input.patch.parentId && input.patch.parentId !== input.state.parentId) {
    changes.push(
      `Коробка-родитель: ${formatTextDiff(input.state.parentId, input.patch.parentId)}`,
    );
  }

  return changes;
}

export async function requestPhotoAutofillSuggestionOperation(input: {
  state: AddBoxWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<AddBoxPhotoAiSuggestion | null> {
  const photo = resolvePhotoForAiAutofill(input.state);
  const transcript = input.state.voiceTranscript.trim();
  const transcriptContext = transcript.length >= 4 ? transcript : "";

  if (!photo) {
    input.state.voiceError =
      "Добавьте фото коробки, чтобы заполнить форму по изображению";
    return null;
  }

  runInAction(() => {
    input.state.isVoiceApplying = true;
    input.state.voiceError = null;
    input.state.voiceInfo = transcriptContext
      ? "Разбираю фото и объединяю с текстом через AI..."
      : "Разбираю фото через AI...";
  });

  try {
    const result = await input.api.fillFormDraft({
      entity: "box",
      transcript: transcriptContext || undefined,
      photo,
    });
    if (result.entity !== "box") {
      throw new Error("Некорректный тип AI-ответа для формы коробки");
    }
    const draft = result.draft as AiBoxDraft;
    const patch = applyAiBoxDraft({
      draft,
      availableBoxes: input.state.availableBoxes,
      currentTypeTags: input.state.typeTags,
      formatParentInputValue: input.formatParentInputValue,
    });
    const changes = buildBoxAiSuggestionChanges({
      state: input.state,
      patch,
    });

    runInAction(() => {
      input.state.voiceInfo = transcriptContext
        ? `AI подготовил правки по фото + тексту (${result.model})`
        : `AI подготовил правки по фото (${result.model})`;
    });

    return {
      model: result.model,
      draft,
      patch,
      changes,
      usedTranscriptContext: !!transcriptContext,
    };
  } catch (error) {
    runInAction(() => {
      input.state.voiceError = resolveAddBoxErrorMessage(
        error,
        "Не удалось заполнить форму по фото",
      );
    });
    return null;
  } finally {
    runInAction(() => {
      input.state.isVoiceApplying = false;
    });
  }
}

export async function setPhotoFromFileOperation(input: {
  state: AddBoxWorkflowState;
  event: ChangeEvent<HTMLInputElement>;
}): Promise<void> {
  const [file] = Array.from(input.event.target.files || []);
  input.event.target.value = "";

  if (!file) {
    return;
  }

  const validationError = validatePhotoFile(file);
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  runInAction(() => {
    input.state.isPhotoLoading = true;
    input.state.error = null;
  });

  try {
    const dataUrl = await fileToDataUrl(file);
    runInAction(() => {
      input.state.photo = dataUrl;
    });
  } catch (error) {
    runInAction(() => {
      input.state.error = resolveAddBoxErrorMessage(
        error,
        "Не удалось загрузить изображение",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isPhotoLoading = false;
    });
  }
}

export async function fillFromVoiceTranscriptOperation(input: {
  state: AddBoxWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<void> {
  const transcript = input.state.voiceTranscript.trim();
  if (transcript.length < 4) {
    input.state.voiceError = "Нужен более подробный текст для автозаполнения";
    return;
  }
  const photo = resolvePhotoForAiAutofill(input.state);

  runInAction(() => {
    input.state.isVoiceApplying = true;
    input.state.voiceError = null;
    input.state.voiceInfo = photo
      ? "Разбираю текст и фото через AI..."
      : "Разбираю текст через AI...";
  });

  try {
    const result = await input.api.fillFormDraft({
      entity: "box",
      transcript,
      photo,
    });
    if (result.entity !== "box") {
      throw new Error("Некорректный тип AI-ответа для формы коробки");
    }
    const draft = result.draft as AiBoxDraft;

    runInAction(() => {
      const patch = applyAiBoxDraft({
        draft,
        availableBoxes: input.state.availableBoxes,
        currentTypeTags: input.state.typeTags,
        formatParentInputValue: input.formatParentInputValue,
      });
      if (patch.name) {
        input.state.name = patch.name;
      }
      if (patch.description) {
        input.state.description = patch.description;
      }
      if (patch.photo) {
        input.state.photo = patch.photo;
      }
      if (patch.typeTags) {
        input.state.typeTags = patch.typeTags;
      }
      if (patch.parentId) {
        input.state.parentId = patch.parentId;
      }
      input.state.voiceInfo = `Черновик заполнен (${result.model})`;
    });
  } catch (error) {
    runInAction(() => {
      input.state.voiceError = resolveAddBoxErrorMessage(
        error,
        "Не удалось заполнить форму из голосового текста",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isVoiceApplying = false;
    });
  }
}

export async function fillFromPhotoOperation(input: {
  state: AddBoxWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<void> {
  const suggestion = await requestPhotoAutofillSuggestionOperation(input);
  if (!suggestion) {
    return;
  }

  if (suggestion.changes.length === 0) {
    runInAction(() => {
      input.state.voiceInfo = "AI не предложил новых изменений по фото";
    });
    return;
  }

  runInAction(() => {
    if (suggestion.patch.name) {
      input.state.name = suggestion.patch.name;
    }
    if (suggestion.patch.description) {
      input.state.description = suggestion.patch.description;
    }
    if (suggestion.patch.photo) {
      input.state.photo = suggestion.patch.photo;
    }
    if (suggestion.patch.typeTags) {
      input.state.typeTags = suggestion.patch.typeTags;
    }
    if (suggestion.patch.parentId) {
      input.state.parentId = suggestion.patch.parentId;
    }
    input.state.voiceInfo = suggestion.usedTranscriptContext
      ? `Черновик заполнен по фото + тексту (${suggestion.model})`
      : `Черновик заполнен по фото (${suggestion.model})`;
  });
}

export function applyBoxPatchOperation(input: {
  state: AddBoxWorkflowState;
  patch: AppliedAiBoxDraft;
}): void {
  if (input.patch.name) {
    input.state.name = input.patch.name;
  }
  if (input.patch.description) {
    input.state.description = input.patch.description;
  }
  if (input.patch.photo) {
    input.state.photo = input.patch.photo;
  }
  if (input.patch.typeTags) {
    input.state.typeTags = input.patch.typeTags;
  }
  if (input.patch.parentId) {
    input.state.parentId = input.patch.parentId;
  }
}

export async function loadAddBoxReferenceDataOperation(input: {
  state: AddBoxWorkflowState;
  api: IWarehouseApi;
  formatParentInputValue: (value: string) => string;
}): Promise<void> {
  runInAction(() => {
    input.state.isReferenceLoading = true;
    input.state.referenceError = null;
  });

  try {
    const boxes = await input.api.getAllBoxes();
    const referenceData = buildReferenceData(boxes);

    runInAction(() => {
      input.state.availableBoxes = referenceData.availableBoxes;
      input.state.typeTagSuggestions = referenceData.typeTagSuggestions;
      input.state.parentId = input.formatParentInputValue(input.state.parentId);
    });
  } catch (error) {
    runInAction(() => {
      input.state.referenceError = resolveAddBoxErrorMessage(
        error,
        "Не удалось загрузить справочные данные",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isReferenceLoading = false;
    });
  }
}

export function clearAddBoxFormOperation(input: {
  state: AddBoxWorkflowState;
  stopVoiceListening: () => void;
  formatParentInputValue: (value: string) => string;
}): void {
  input.stopVoiceListening();
  input.state.name = "";
  input.state.description = "";
  input.state.photo = "";
  input.state.typeTags = [];
  input.state.typeTagInput = "";
  input.state.parentId = input.formatParentInputValue("");
  input.state.error = null;
  input.state.isPhotoLoading = false;
  input.state.isVoicePanelOpen = false;
  input.state.voiceTranscript = "";
  input.state.voiceError = null;
  input.state.voiceInfo = null;
}

export function goBackFromAddBoxOperation(input: {
  returnToAddProduct: boolean;
  productDraftForReturn: AddProductDraft | null;
  navigationStore: INavigationStore;
}): void {
  if (input.returnToAddProduct) {
    input.navigationStore.setScreen(Screens.AddProduct, {
      draft: input.productDraftForReturn || undefined,
    });
    return;
  }

  input.navigationStore.setScreen(Screens.Main);
}

export async function submitAddBoxOperation(input: {
  state: AddBoxWorkflowState;
  api: IWarehouseApi;
  store: IStore;
  navigationStore: INavigationStore;
  returnToAddProduct: boolean;
  productDraftForReturn: AddProductDraft | null;
  onCreated?: (box: Box) => void | boolean | Promise<void | boolean>;
}): Promise<void> {
  const validationError = validateBoxForm({
    name: input.state.name,
    description: input.state.description,
    photo: input.state.photo,
    typeTags: input.state.typeTags,
    parentInput: input.state.parentId,
    availableBoxes: input.state.availableBoxes,
  });
  if (validationError) {
    input.state.error = validationError;
    return;
  }

  runInAction(() => {
    input.state.isSubmitting = true;
    input.state.error = null;
  });

  try {
    const payload = buildCreateBoxPayload({
      name: input.state.name,
      description: input.state.description,
      photo: input.state.photo,
      typeTags: input.state.typeTags,
      parentInput: input.state.parentId,
      availableBoxes: input.state.availableBoxes,
    });
    const created = await input.api.createBox(payload);
    const box = new Box(created);

    runInAction(() => {
      input.store.addBox(box);
    });
    const shouldContinue = input.onCreated
      ? await input.onCreated(box)
      : undefined;
    if (shouldContinue === false) {
      return;
    }
    if (input.returnToAddProduct) {
      input.navigationStore.setScreen(Screens.AddProduct, {
        parentId: box.id,
        draft: input.productDraftForReturn || undefined,
      });
      return;
    }
    input.navigationStore.setScreen(Screens.Box, box);
  } catch (error) {
    runInAction(() => {
      input.state.error = resolveAddBoxErrorMessage(
        error,
        "Не удалось создать коробку",
      );
    });
  } finally {
    runInAction(() => {
      input.state.isSubmitting = false;
    });
  }
}
